import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { resolveDebtData } from './consts'
import Breadcrumb from '../../shared/Breadcrumb'
import SidebarOptions from './Options'
import ResolveDebt from '../../shared/ResolveDebt'
import RichText from '../../shared/RichText'
import H1 from '../../shared/Headings/H1'
import Accordion from '../../shared/FAQ/Accordion'
import { responseToDebtStatesProps } from './responseToDebtStatesProps'
import ContentTemplate from '../../shared/ContentTemplate'
import styles from './index.module.scss'
import CustomJsonLd from '../../shared/CustomJsonLd'
import { addHyperlinkTitle } from '../../../common/helpers'
import { APP_BASE_PATH } from '../../../common/constants'

const DebtConsolidationStatePage = ({
  content,
  faq,
  title,
  keyTakeaways,
  state,
  cities,
  breadcrumbs,
  modifiedDate,
  author,
  updatedAt,
  metaDescription,
  metaTitle,
  imageAsset,
  pageSlug,
  jsonLdId,
  dateCreated,
}) => {
  const jsonLdData = useMemo(() => {
    const webPageData = {
      '@context': 'https://schema.org/',
      '@type': 'WebPage',
      '@id': `https://www.freedomdebtrelief.com/${pageSlug}#webpage`,
      name: metaTitle,
      url: `${APP_BASE_PATH()}${pageSlug}`,
      inLanguage: 'en-US',
      description: metaDescription,
      lastReviewed: updatedAt,
      dateCreated: dateCreated,
    }

    if (author.fullName) {
      const sameAs = author.socialMedia.filter(
        (el) => el !== null && el !== undefined
      )
      const articleData = {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        mainEntityOfPage: `https://www.freedomdebtrelief.com/${pageSlug}`,
        headline: title,
        description: metaDescription,
        keywords: keyTakeaways,
        datePublished: dateCreated,
        dateModified: updatedAt,
        author: {
          '@type': 'Person',
          name: author.fullName,
          description: author.description,
          url: `${APP_BASE_PATH()}${author.url}`,
          sameAs: sameAs,
          image: {
            '@type': 'ImageObject',
            url: author.image,
            height: 200,
            width: 200,
          },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Freedom Debt Relief',
          legalName: 'Freedom Debt Relief LLC',
          description:
            "Freedom Debt Relief is America's leader in credit card debt relief and debt consolidation to help clients lower their monthly payments without bankruptcy or loans.",
          url: 'https://www.freedomdebtrelief.com/#organization',
          sameAs: [
            'https://twitter.com/freedomdebt',
            'https://www.facebook.com/freedomdebtrelief/',
            'https://www.linkedin.com/company/freedom-debt-relief/',
            'https://www.youtube.com/freedomdebtrelief',
            'https://www.instagram.com/freedomdebtrelief/',
            'https://www.google.com/search?kgmid=/g/11stymh6d3',
            'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9 ',
          ],
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
            width: '160',
            height: '50',
          },
        },
      }
      if (imageAsset.url) {
        return Object.assign(articleData, {
          image: [
            {
              '@type': 'ImageObject',
              '@id':
                'https://www.freedomdebtrelief.com/debt-relief/#primaryimage',
              url: imageAsset?.url,
              width: imageAsset?.details?.image?.width,
              height: imageAsset?.details?.image?.height,
              caption: imageAsset?.title,
            },
          ],
        })
      } else return articleData
    } else return webPageData
  }, [
    updatedAt,
    metaDescription,
    metaTitle,
    pageSlug,
    author,
    imageAsset,
    title,
    keyTakeaways,
    dateCreated,
  ])

  addHyperlinkTitle(content.content, title)

  return (
    <>
      <CustomJsonLd id={jsonLdId} data={jsonLdData} />
      <div
        className="w-content gap-x-primary padding-x-primary grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12"
        data-testid="debt-consolidation-state"
      >
        <div className="col-span-full p-4 md:col-start-1 md:px-3 lg:col-start-1 lg:px-2 xl:col-start-2">
          <Breadcrumb items={breadcrumbs} />
        </div>
        <div
          className="col-span-full col-start-1 px-2 md:col-start-1  lg:col-start-1 lg:flex lg:px-0 xl:col-start-2"
          data-testid="page-content"
        >
          <div
            className="px-3 pt-4 text-sm md:px-0 lg:mr-18 lg:w-3/5"
            data-testid="page-left-side"
          >
            <H1
              text={title}
              id={title}
              className="mt-5 text-black-base lg:mt-4"
            />
            {author.fullName && author.url ? (
              <ContentTemplate.Author
                fullName={author.fullName}
                url={author.url}
              />
            ) : null}
            <ContentTemplate.ModifiedDate
              className="my-4 text-base"
              date={modifiedDate}
            />
            {keyTakeaways?.length ? (
              <ContentTemplate.KeyTakeaways list={keyTakeaways} />
            ) : null}
            <div className="padding-t-primary" data-testid="content">
              <RichText contentData={content} className={styles.richText} />
            </div>
            {faq?.length ? (
              <div className="margin-t-primary" data-testid="faq">
                <Accordion
                  showFirstDivider
                  questionClassName="lg:pb-8"
                  title="Frequently Asked Questions"
                  items={faq}
                />
              </div>
            ) : null}
          </div>

          <div
            className="px-2 pt-4 lg:w-3/10 lg:px-0"
            data-testid="page-right-side"
          >
            <ResolveDebt data={resolveDebtData} />
            <SidebarOptions
              options={cities.map((c) => ({
                url: `/debt-consolidation-near-me/${c.slug}/`,
                title: c.city,
              }))}
              title={`${state ? state.state + ' Cities' : ''}`}
              className="pt-8"
              stateSlug={state?.slug}
            />
          </div>
        </div>
      </div>
    </>
  )
}

DebtConsolidationStatePage.defaultProps = {
  content: {},
  state: {},
  cities: [],
  title: '',
  isState: false,
  faq: [],
  keyTakeaways: [],
  metaDescription: '',
  metaTitle: '',
  updatedAt: '',
  imageAsset: {},
  pageSlug: '',
  dateCreated: '',
}

DebtConsolidationStatePage.propTypes = {
  content: PropTypes.object,
  state: PropTypes.object,
  cities: PropTypes.array,
  title: PropTypes.string,
  isState: PropTypes.bool,
  faq: PropTypes.array,
  keyTakeaways: PropTypes.array,
  jsonLdId: PropTypes.string,
  metaDescription: PropTypes.string,
  metaTitle: PropTypes.string,
  updatedAt: PropTypes.string,
  imageAsset: PropTypes.object,
  pageSlug: PropTypes.string,
  dateCreated: PropTypes.string,
}

const DebtConsolidationStates = responseToDebtStatesProps(
  DebtConsolidationStatePage
)

export default React.memo(DebtConsolidationStates)
